/**
 * `useFileUpload` — sibling to `useImageUpload` for non-image
 * (and image) attachments where the form ships the resulting
 * `attachmentId` to the BE on Submit, rather than embedding a URL
 * inline.
 *
 * Flow (`parentKind='client'` and similar two-phase surfaces):
 *   1. POST /api/uploads/presign — { parentKind, parentId: undefined, ... }
 *   2. PUT  <s3-presigned-url>  — direct browser → S3.
 *   3. caller stores `attachmentId` in form state; on submit, the
 *      attachmentIds[] array goes to the BE which calls
 *      `/api/uploads/{id}/attach` inside the same tenant transaction.
 *
 * `useFetchSignedUrl` returns a presigned GET URL for an existing
 * attachment so chips can render a "Download" link. Short-lived
 * (5-min TTL) — the caller refreshes on each click rather than caching.
 */

import { ApiError, apiClient } from '@/api/client'
import { useCallback } from 'react'
import { type PutProgress, putWithProgress } from './put-with-progress'
import type { ParentKind } from './types'
import { markUploadFinished, markUploadStarted } from './upload-registry'

/**
 * Per-call options. Optional so the ~10 existing call sites keep working
 * untouched and each surface opts into progress when its UI is ready.
 */
export interface UploadCallOptions {
  signal?: AbortSignal
  onProgress?: (progress: PutProgress) => void
  /**
   * Fired with the attachment id the moment presign returns — BEFORE the
   * PUT. The BE charges the bytes at presign, so a caller that tracks
   * which rows it created (to release them later) must learn the id here:
   * a cancelled or failed PUT never reaches the resolved result.
   */
  onPresigned?: (attachmentId: string) => void
}

export interface FileUploadResult {
  attachmentId: string
  s3Key: string
  /** Echoed from the file the user picked. */
  filename: string
  contentType: string
  sizeBytes: number
}

interface PresignResponse {
  data: {
    key: string
    uploadUrl: string
    requiredHeaders: Record<string, string>
    attachmentId: string
    publicUrl: string | null
  }
}

interface SignedGetResponse {
  data: {
    url: string
    contentType: string
    originalFilename: string
  }
}

export interface UseFileUploadOptions {
  parentKind: ParentKind
  /**
   * Optional. Omit for two-phase uploads where the caller will pass
   * the resulting `attachmentId` to the BE form-submit endpoint.
   */
  parentId?: string | null
}

export const useFileUpload = ({ parentKind, parentId }: UseFileUploadOptions) => {
  const uploadFile = useCallback(
    async (file: File, options?: UploadCallOptions): Promise<FileUploadResult> => {
      const presign = await apiClient<PresignResponse>('/api/uploads/presign', {
        method: 'POST',
        body: {
          parentKind,
          parentId: parentId ?? undefined,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        },
        signal: options?.signal,
      })
      const { uploadUrl, requiredHeaders, attachmentId, key } = presign.data
      options?.onPresigned?.(attachmentId)

      // Registration goes HERE, in the hook, rather than at a call site.
      // It was previously wired into the wiki orchestrator's tracking on
      // the belief that every upload passed through it — but the
      // slash-command modals call this hook directly and never touch the
      // orchestrator, so refreshing mid-upload silently lost the file with
      // no warning. Registering at the one point every caller shares is
      // what makes the unload guard cover all of them by construction.
      //
      // Keyed on the attachment id, which only exists after presign; there
      // is nothing to lose before that, since the bytes have not started.
      markUploadStarted(attachmentId)
      try {
        const put = await putWithProgress({
          url: uploadUrl,
          file,
          headers: requiredHeaders,
          signal: options?.signal,
          onProgress: options?.onProgress,
        })
        if (put.status < 200 || put.status >= 300) {
          throw new ApiError(
            `S3 upload failed: ${put.status} ${put.statusText}`,
            put.status,
            put.responseText,
          )
        }
      } finally {
        // `finally`, not a trailing call: a missed exit leaks an entry into
        // a module-scope registry that nothing collects, and the tab then
        // warns about a phantom upload on every reload for the rest of its
        // life.
        markUploadFinished(attachmentId)
      }

      return {
        attachmentId,
        s3Key: key,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      }
    },
    [parentKind, parentId],
  )

  return { uploadFile }
}

/**
 * Fetch a fresh presigned GET URL for an attachment. Short-lived;
 * call this each time a download chip is clicked rather than caching.
 */
export async function fetchSignedAttachmentUrl(attachmentId: string): Promise<string> {
  const res = await apiClient<SignedGetResponse>(`/api/uploads/${attachmentId}/url?as=json`, {
    method: 'GET',
  })
  return res.data.url
}

/**
 * Soft-delete a (still-pending) attachment from the FE side — used
 * when the user removes a file from the form before submitting. The
 * BE will sweep orphan rows on its own schedule, but this gives the
 * UX an immediate, durable "I removed it" signal so a stale draft
 * can't accidentally re-attach a discarded file.
 */
export async function deletePendingAttachment(attachmentId: string): Promise<void> {
  try {
    await apiClient(`/api/uploads/${attachmentId}`, { method: 'DELETE' })
  } catch (err) {
    // Best-effort — a 404 (already gone, swept, or sister tab cleared
    // it) is fine.
    if (err instanceof ApiError && err.status === 404) return
    throw err
  }
}
